import React from 'react';
import kebabCase from 'lodash/kebabCase';
import { Section, SkeletonItem } from '@atlaskit/side-navigation';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useBoard } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import ProjectKeyboardShortcuts from '@atlassian/jira-software-keyboard-shortcuts/src/ui/project-shortcuts/index.tsx';
import { usePathParam, useQueryParam } from '@atlassian/react-resource-router';
import { useReports } from '../../../controllers/reports/index.tsx';
import messages from './messages.tsx';
import type { ReportsProps } from './types.tsx';

const useThrowReports = withThrowOnError(useReports);
const useThrowBoard = withThrowOnError(useBoard);

export const Reports = ({ enableKeyboardShortcuts }: ReportsProps) => {
	const [paramAccountId] = usePathParam('accountId');
	const [isLocationlessBoard] = useQueryParam('locationless');
	const { data: board } = useThrowBoard();
	const { data: reports } = useThrowReports();
	const { formatMessage } = useIntl();

	if (board == null || reports == null) return <SkeletonItem />;

	const boardAccountId = paramAccountId ?? '';
	const shouldUsePrettyUrl = !isLocationlessBoard;

	return (
		<Section title={formatMessage(messages.reports)}>
			{fg('reports-overview-page-migration') && (
				<MenuItem
					analytics={{ itemId: 'all-reports' }}
					href={`/jira/people/${boardAccountId}/boards/${board.id}/reports`}
					key="all-reports"
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.allReports)}
				</MenuItem>
			)}
			{reports.map(({ chart, id, message }) => (
				<MenuItem
					analytics={{ itemId: `report-${id}` }}
					href={
						shouldUsePrettyUrl
							? `/jira/people/${boardAccountId}/boards/${board.id}/reports/${kebabCase(chart)}`
							: `/secure/RapidBoard.jspa?rapidView=${board.id}&view=reporting&chart=${chart}&locationless=true`
					}
					key={id}
					selectedOn={
						shouldUsePrettyUrl ? matchUrlPathname() : ({ query }) => query.chart === chart
					}
				>
					{formatMessage(message)}
				</MenuItem>
			))}
			{enableKeyboardShortcuts && !isLocationlessBoard && getWillShowNav3() ? (
				<ProjectKeyboardShortcuts boardId={board.id} projectKey="" />
			) : null}
		</Section>
	);
};
